<template>
  <div class="w-full">
    <div class="flex justify-center">
      <div
        ref="menuContainerRef"
        class="relative flex items-center justify-evenly md:justify-center backdrop-blur-md bg-white/100 md:bg-white/10 border border-white/30 md:border-white/20 drop-shadow-x1 md:rounded-full w-full md:w-auto px-1 md:px-2 py-1 md:py-2 gap-0 md:gap-0"
      >
        <transition name="slider-fade">
          <div
            v-if="sliderStyle.width"
            class="absolute rounded-full bg-gray-50 transition-all duration-600 ease-in-out hidden md:block"
            :style="sliderStyle"
            aria-hidden="true"
          ></div>
        </transition>

        <button
          v-for="item in items"
          :key="item.value"
          :ref="(el) => setButtonRef(item.value, el)"
          @click="handleSelect(item.value)"
          :disabled="isItemDisabled(item)"
          class="relative z-10 flex-1 md:flex-initial px-0 md:px-6 py-1 md:py-2 text-sm md:text-sm font-normal transition-all duration-300 ease-in-out rounded-full text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4ECF84] max-w-[16.666%] md:max-w-none md:whitespace-nowrap"
          :class="[
            isItemDisabled(item)
              ? 'text-gray-400 cursor-not-allowed opacity-50'
              : (activeValue === item.value
                ? 'text-[#4ECF84]'
                : 'hover:text-[#4ECF84]')
          ]"
        >
          <span class="md:hidden flex flex-col items-center gap-0.5 font-medium">
            <Icon v-if="item.icon" :name="item.icon" class="w-6 h-6 stroke-[1.5]" />
            <span class="text-xs mt-0.5 h-4 overflow-visible whitespace-nowrap" :class="activeValue === item.value ? 'opacity-100' : 'opacity-0'">
              {{ item.mobileLabel || item.label }}
            </span>
          </span>
          <span class="hidden md:inline inline-flex items-center gap-2">
            <Icon v-if="item.icon" :name="item.icon" class="w-4 h-4" />
            {{ item.label }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Icon from '@/components/Icon.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: 'TripHub'
  },
  items: {
    type: Array,
    default: () => [
      { value: 'TripHub', label: 'TripHub', mobileLabel: 'TripHub', icon: 'target' },
      { value: 'Rewards', label: 'Rewards', icon: 'award' },
      { value: 'Community', label: 'Community', icon: 'message-square-share' },
      { value: 'Subscription', label: 'Subscription', icon: 'badge-check' },
      { value: 'Alerts', label: 'Alerts', icon: 'bell-ring' },
      { value: 'Profile', label: 'Profile', icon: 'user-round-cog' }
    ]
  },
  isVerified: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue', 'select'])
const router = useRouter()

const activeValue = computed(() => props.modelValue)
const items = computed(() => props.items || [])

const menuContainerRef = ref(null)
const buttonRefs = ref({})
const sliderStyle = ref({})

const setButtonRef = (id, el) => {
  if (el) {
    buttonRefs.value[id] = el
  }
}

const handleSelect = (value) => {
  // Don't allow navigation if item is disabled
  const item = items.value.find(i => i.value === value)
  if (isItemDisabled(item)) {
    return
  }
  
  // Emit the update first to update the active state
  emit('update:modelValue', value)
  emit('select', value)
  
  // Handle navigation based on the selected value
  const routeMap = {
    'Profile': { name: 'Profile' },
    'TripHub': { name: 'TripHub' },
    'Rewards': { name: 'Rewards' },
    'Subscription': { name: 'Subscription' },
    'Alerts': { name: 'Alerts' },
    'Community': { name: 'Community' }
  }
  
  const route = routeMap[value]
  if (route && router.currentRoute.value.name !== route.name) {
    router.push(route)
  }
}

const isItemDisabled = (item) => {
  // Only Profile is accessible when unverified
  // Check if development bypass is enabled
  const devBypassEnabled = import.meta.env.DEV && 
    (localStorage.getItem('dev_bypass_verification') !== 'false')
  
  if (!props.isVerified && item.value !== 'Profile' && !devBypassEnabled) {
    return true
  }
  return false
}

const updateSlider = () => {
  const container = menuContainerRef.value
  const activeButton = buttonRefs.value[activeValue.value]

  if (!container || !activeButton) return

  const containerRect = container.getBoundingClientRect()
  const buttonRect = activeButton.getBoundingClientRect()
  const isMobile = window.matchMedia('(max-width: 767px)').matches

  if (isMobile) {
    sliderStyle.value = {}
  } else {
    sliderStyle.value = {
      left: `${buttonRect.left - containerRect.left}px`,
      width: `${buttonRect.width}px`,
      top: '8px',
      bottom: '8px'
    }
  }
}

const refreshSlider = async () => {
  await nextTick()
  updateSlider()
}

watch(activeValue, () => {
  refreshSlider()
})

watch(items, () => {
  refreshSlider()
})

onMounted(() => {
  refreshSlider()
  window.addEventListener('resize', updateSlider)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateSlider)
})

// Expose programmatic control
defineExpose({
  setActive: async (value) => {
    emit('update:modelValue', value)
    await refreshSlider()
  }
})
</script>

<style scoped>
.slider-fade-enter-active,
.slider-fade-leave-active {
  transition: opacity 300ms ease-in-out;
}

.slider-fade-enter-from,
.slider-fade-leave-to {
  opacity: 0;
}
</style>
